import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Input,
  Select,
  DatePicker,
  Statistic,
  Row,
  Col,
  Dropdown,
  Typography,
  Empty
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  DollarOutlined,
  StopOutlined,
  RollbackOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import api, { getApiErrorMessage } from '../utils/api'

const { Title } = Typography
const { Search } = Input
const { Option } = Select
const { RangePicker } = DatePicker

// ── 樣式 ────────────────────────────────────────────────
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`

const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;

  .ant-card-body {
    padding: 20px;

    @media (max-width: 768px) {
      padding: 16px 12px;
    }
  }
`

const StatsRow = styled(Row)`
  margin-bottom: 20px;
`

const StatCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;

  .ant-card-body {
    padding: 16px 20px;
  }
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`

const FilterItem = styled.div`
  flex: 1;
  min-width: 180px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`

// ── 主元件 ──────────────────────────────────────────────

const PaymentRecordsPage = () => {
  const { user } = useAuth()
  const businessId = user?.businessId

  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [stats, setStats] = useState({
    totalAmount: 0,
    completedAmount: 0,
    totalCount: 0,
    completedCount: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: undefined,
    startDate: undefined,
    endDate: undefined
  })
  const [actionLoading, setActionLoading] = useState({})

  // ── 取得支付紀錄 ──────────────────────────────────────
  const fetchRecords = useCallback(
    async (page = 1, pageSize = 20) => {
      if (!businessId) return
      setLoading(true)
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: pageSize.toString()
        })
        if (filters.search?.trim()) params.append('search', filters.search.trim())
        if (filters.status) params.append('status', filters.status)
        if (filters.startDate) params.append('startDate', filters.startDate)
        if (filters.endDate) params.append('endDate', filters.endDate)

        const response = await api.get(
          `/api/payments/business/${businessId}/records?${params.toString()}`
        )

        if (response.data.success) {
          setRecords(response.data.data || [])
          setPagination({
            current: response.data.pagination?.currentPage || page,
            pageSize: response.data.pagination?.itemsPerPage || pageSize,
            total: response.data.pagination?.totalItems || 0
          })
          setStats(
            response.data.stats || {
              totalAmount: 0,
              completedAmount: 0,
              totalCount: 0,
              completedCount: 0
            }
          )
        }
      } catch (error) {
        console.error('獲取支付紀錄失敗:', error)
        message.error(getApiErrorMessage(error, '獲取支付紀錄失敗'))
      } finally {
        setLoading(false)
      }
    },
    [businessId, filters]
  )

  useEffect(() => {
    fetchRecords(1, pagination.pageSize)
  }, [filters, businessId])

  // ── 格式化工具 ────────────────────────────────────────
  const formatAmount = (amount) =>
    `NT$ ${Number(amount || 0).toLocaleString()}`

  const statusConfig = {
    pending: { text: '處理中', color: 'warning' },
    completed: { text: '已完成', color: 'success' },
    failed: { text: '失敗', color: 'error' },
    refunded: { text: '已退款', color: 'default' }
  }

  const paymentForConfig = {
    booking: { text: '預約', color: 'blue' },
    product: { text: '商品', color: 'purple' },
    quick_payment: { text: '快速收款', color: 'cyan' }
  }

  // ── 信用卡操作 ────────────────────────────────────────
  const handleCancelAuth = async (record) => {
    setActionLoading((prev) => ({ ...prev, [record._id]: 'cancel' }))
    try {
      const response = await api.post('/api/payments/credit-card/cancel', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        business_id: businessId
      })
      if (response.data.success) {
        message.success(response.data.message || '取消授權成功')
        fetchRecords(pagination.current, pagination.pageSize)
      } else {
        message.error(response.data.message || '取消授權失敗')
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, '取消授權失敗'))
    } finally {
      setActionLoading((prev) => ({ ...prev, [record._id]: null }))
    }
  }

  const handleRefund = async (record) => {
    if (!record.trade_no) {
      message.error('缺少藍新交易序號，無法退款')
      return
    }
    setActionLoading((prev) => ({ ...prev, [record._id]: 'refund' }))
    try {
      const response = await api.post('/api/payments/credit-card/close', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        close_type: 2,
        business_id: businessId
      })
      if (response.data.success) {
        message.success(response.data.message || '退款成功')
        fetchRecords(pagination.current, pagination.pageSize)
      } else {
        message.error(response.data.message || '退款失敗')
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, '退款失敗'))
    } finally {
      setActionLoading((prev) => ({ ...prev, [record._id]: null }))
    }
  }

  const handleQueryTrade = async (record) => {
    setActionLoading((prev) => ({ ...prev, [record._id]: 'query' }))
    try {
      const response = await api.post('/api/payments/credit-card/query', {
        order_sn: record.order_sn,
        trade_no: record.trade_no,
        amount: record.amount,
        business_id: businessId
      })
      if (response.data.success) {
        const d = response.data.data
        Modal.info({
          title: '交易查詢結果',
          width: 520,
          content: (
            <div style={{ marginTop: 12 }}>
              <p>
                <b>交易狀態：</b>
                {d.TradeStatus === '1'
                  ? '已付款'
                  : d.TradeStatus === '0'
                    ? '未付款'
                    : d.TradeStatus}
              </p>
              <p>
                <b>交易序號：</b>
                {d.TradeNo || '-'}
              </p>
              <p>
                <b>金額：</b>NT$ {d.Amt || record.amount}
              </p>
              <p>
                <b>付款方式：</b>
                {d.PaymentType || '-'}
              </p>
              <p>
                <b>付款時間：</b>
                {d.PayTime || '-'}
              </p>
              <p>
                <b>授權碼：</b>
                {d.Auth || '-'}
              </p>
              <p>
                <b>請款狀態：</b>
                {d.CloseStatus === '0'
                  ? '未請款'
                  : d.CloseStatus === '3'
                    ? '已請款'
                    : d.CloseStatus || '-'}
              </p>
              <p>
                <b>退款狀態：</b>
                {d.BackStatus === '0'
                  ? '未退款'
                  : d.BackStatus === '3'
                    ? '已退款'
                    : d.BackStatus || '-'}
              </p>
            </div>
          )
        })
      } else {
        message.error(response.data.message || '查詢失敗')
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, '查詢交易失敗'))
    } finally {
      setActionLoading((prev) => ({ ...prev, [record._id]: null }))
    }
  }

  // ── 表格欄位 ──────────────────────────────────────────
  const columns = [
    {
      title: '訂單編號',
      dataIndex: 'order_sn',
      key: 'order_sn',
      width: 170,
      ellipsis: true,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{text}</span>
      )
    },
    {
      title: '類型',
      dataIndex: 'payment_for',
      key: 'payment_for',
      width: 90,
      render: (v) => {
        const c = paymentForConfig[v] || { text: v, color: 'default' }
        return <Tag color={c.color}>{c.text}</Tag>
      }
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (v) => (
        <span style={{ fontWeight: 500 }}>{formatAmount(v)}</span>
      )
    },
    {
      title: '客戶',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
      width: 100,
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v) => {
        const c = statusConfig[v] || { text: v, color: 'default' }
        return <Tag color={c.color}>{c.text}</Tag>
      }
    },
    {
      title: '交易序號',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 170,
      ellipsis: true,
      render: (text) =>
        text ? (
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</span>
        ) : (
          '-'
        )
    },
    {
      title: '卡號',
      dataIndex: 'card_last_four',
      key: 'card_last_four',
      width: 80,
      render: (text) =>
        text ? (
          <span style={{ fontFamily: 'monospace' }}>*{text}</span>
        ) : (
          '-'
        )
    },
    {
      title: '支付時間',
      dataIndex: 'pay_time',
      key: 'pay_time',
      width: 140,
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => {
        // 只有信用卡交易才有操作
        if (record.payment_type !== 'credit') return null

        const loading = actionLoading[record._id]
        const menuItems = [
          {
            key: 'query',
            icon: <SearchOutlined />,
            label: '查詢交易狀態',
            onClick: () => handleQueryTrade(record)
          }
        ]

        if (record.status === 'completed') {
          menuItems.push(
            {
              key: 'cancel',
              icon: <StopOutlined />,
              label: '取消授權',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: '確認取消授權',
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      <p>確定要取消此筆交易的授權嗎？</p>
                      <p>
                        訂單：{record.order_sn}
                        <br />
                        金額：{formatAmount(record.amount)}
                      </p>
                      <p style={{ color: '#999', fontSize: 13 }}>
                        取消授權後款項將不會從客戶帳戶扣除。
                      </p>
                    </div>
                  ),
                  okText: '確認取消',
                  cancelText: '返回',
                  okType: 'danger',
                  onOk: () => handleCancelAuth(record)
                })
              }
            },
            {
              key: 'refund',
              icon: <RollbackOutlined />,
              label: '退款',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: '確認退款',
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      <p>確定要對此筆交易進行退款嗎？</p>
                      <p>
                        訂單：{record.order_sn}
                        <br />
                        金額：{formatAmount(record.amount)}
                      </p>
                      <p style={{ color: '#999', fontSize: 13 }}>
                        退款後款項將退回至客戶信用卡。
                      </p>
                    </div>
                  ),
                  okText: '確認退款',
                  cancelText: '返回',
                  okType: 'danger',
                  onOk: () => handleRefund(record)
                })
              }
            }
          )
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button size='small' loading={!!loading}>
              操作 ▾
            </Button>
          </Dropdown>
        )
      }
    }
  ]

  // ── 渲染 ──────────────────────────────────────────────
  return (
    <PageContainer>
      <Title level={3} style={{ marginBottom: 20 }}>
        支付紀錄
      </Title>

      {/* 統計卡片 */}
      <StatsRow gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <StatCard>
            <Statistic
              title='總交易金額'
              value={stats.totalAmount}
              prefix={<DollarOutlined />}
              formatter={(v) => formatAmount(v)}
            />
          </StatCard>
        </Col>
        <Col xs={12} sm={6}>
          <StatCard>
            <Statistic
              title='已完成金額'
              value={stats.completedAmount}
              prefix={<DollarOutlined />}
              formatter={(v) => formatAmount(v)}
              valueStyle={{ color: '#52c41a' }}
            />
          </StatCard>
        </Col>
        <Col xs={12} sm={6}>
          <StatCard>
            <Statistic title='總筆數' value={stats.totalCount} />
          </StatCard>
        </Col>
        <Col xs={12} sm={6}>
          <StatCard>
            <Statistic
              title='已完成筆數'
              value={stats.completedCount}
              valueStyle={{ color: '#52c41a' }}
            />
          </StatCard>
        </Col>
      </StatsRow>

      {/* 篩選 */}
      <StyledCard>
        <FilterRow>
          <FilterItem>
            <Search
              placeholder='搜尋訂單號、客戶姓名、電話、交易號'
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={(v) => {
                setFilters((prev) => ({ ...prev, search: v }))
                setPagination((prev) => ({ ...prev, current: 1 }))
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  setFilters((prev) => ({ ...prev, search: '' }))
                }
              }}
            />
          </FilterItem>
          <FilterItem style={{ maxWidth: 160 }}>
            <Select
              placeholder='狀態'
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => {
                setFilters((prev) => ({ ...prev, status: v }))
                setPagination((prev) => ({ ...prev, current: 1 }))
              }}
            >
              <Option value='pending'>處理中</Option>
              <Option value='completed'>已完成</Option>
              <Option value='failed'>失敗</Option>
              <Option value='refunded'>已退款</Option>
            </Select>
          </FilterItem>
          <FilterItem>
            <RangePicker
              placeholder={['開始日期', '結束日期']}
              style={{ width: '100%' }}
              format='YYYY-MM-DD'
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  setFilters((prev) => ({
                    ...prev,
                    startDate: dates[0].format('YYYY-MM-DD'),
                    endDate: dates[1].format('YYYY-MM-DD')
                  }))
                } else {
                  setFilters((prev) => ({
                    ...prev,
                    startDate: undefined,
                    endDate: undefined
                  }))
                }
                setPagination((prev) => ({ ...prev, current: 1 }))
              }}
            />
          </FilterItem>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters({
                search: '',
                status: undefined,
                startDate: undefined,
                endDate: undefined
              })
              setPagination((prev) => ({ ...prev, current: 1 }))
            }}
          >
            重置
          </Button>
        </FilterRow>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={records}
          rowKey='_id'
          loading={loading}
          scroll={{ x: 1000 }}
          size='small'
          locale={{
            emptyText: (
              <Empty
                description='尚無支付紀錄'
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 筆`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }))
              fetchRecords(page, pageSize)
            }
          }}
        />
      </StyledCard>
    </PageContainer>
  )
}

export default PaymentRecordsPage
